import axios from "axios";
import { getJobApiCall, getJobApiTextCall } from "../../utils/jobRequest";
import { handle403Logout } from "../../utils/request";

/**
 * Mutations for the idibu settings. jobRequest's put/delete helpers throw a TypeError
 * on 4xx (returnErrors is called without dispatch), which hides the server's message.
 */
const idibuMutation = (method, url, body) => {
  const token = localStorage.getItem("token");
  return axios({
    method,
    url: process.env.REACT_APP_AI_API_KEY + url,
    data: body,
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "X-User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });
};

const apiErrorDetail = (errOrRes, fallback) => {
  const data = errOrRes?.response?.data || errOrRes?.originalError?.response?.data;
  const detail = data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return data?.message || errOrRes?.parsedMessage || fallback;
};

/** Normalise jobRequest results (some helpers resolve with the axios error on 4xx). */
const toResult = (res, fallback) => {
  if (res?.status >= 200 && res?.status < 300) {
    return { success: true, data: res.data?.data ?? res.data };
  }
  return { success: false, message: apiErrorDetail(res, fallback), status: res?.response?.status };
};

export const fetchIdibuStatus = () => async () => {
  try {
    const res = await getJobApiCall("idibu/status");
    if (res?.status === 200) {
      return res.data?.data ?? res.data;
    }
    return { effective_enabled: false };
  } catch (error) {
    handle403Logout(error?.originalError || error);
    return { effective_enabled: false };
  }
};

export const fetchIdibuIframeHtml = (jobId) => async () => {
  try {
    const res = await getJobApiTextCall(`idibu/jobs/${jobId}/iframe-frame`);
    if (res?.status === 200 && typeof res.data === "string") {
      return { success: true, html: res.data };
    }
    return {
      success: false,
      message: res?.data?.detail || "Failed to open Idibu posting",
    };
  } catch (error) {
    // Text response: handle403Logout can't read the JSON detail, so only expire on 401.
    if (error?.parsedStatus === 401) handle403Logout(error?.originalError || error);
    let message = error?.parsedMessage;
    try {
      const body = JSON.parse(error?.originalError?.response?.data || "{}");
      message = body?.detail || message;
    } catch (_) {
      // non-JSON error body
    }
    return {
      success: false,
      status: error?.parsedStatus,
      message: message || "Failed to open Idibu posting",
    };
  }
};

/** Tenant idibu account (Settings > Integration). Secrets are never returned. */
export const fetchIdibuIntegration = () => async () => {
  try {
    const res = await getJobApiCall("idibu/integration");
    return toResult(res, "Failed to load idibu integration");
  } catch (error) {
    handle403Logout(error?.originalError || error);
    return {
      success: false,
      status: error?.parsedStatus,
      message: apiErrorDetail(error, "Failed to load idibu integration"),
    };
  }
};

const runMutation = async (method, url, body, fallback) => {
  try {
    const res = await idibuMutation(method, url, body);
    return toResult(res, fallback);
  } catch (error) {
    handle403Logout(error);
    return {
      success: false,
      status: error?.response?.status,
      message: apiErrorDetail(error, fallback),
    };
  }
};

export const saveIdibuIntegration = (payload) => () =>
  runMutation("put", "idibu/integration", payload, "Failed to save idibu details");

export const testIdibuIntegration = () => () =>
  runMutation("post", "idibu/integration/test", {}, "Connection test failed");

export const disconnectIdibuIntegration = () => () =>
  runMutation("delete", "idibu/integration", undefined, "Failed to disconnect idibu");

export const requestIdibuIntegrationHelp = (payload) => () =>
  runMutation("post", "idibu/integration/request", payload, "Failed to send request");

export const requestZapierIntegration = (payload) => () =>
  runMutation("post", "integrations/zapier/contact", payload, "Failed to send request");
